import React from 'react';
import Dialog from '@mui/material/Dialog';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import IconButton from '@mui/material/IconButton';
import Summary from './Summary';
import RiskReviews from 'pages/Finance/Components/Financing/common/Risk';
import Comitee from 'pages/Finance/Components/Financing/common/Comittee';
import File from './File';
import EditableSummary from './EditableSummary';
import { Close } from '@mui/icons-material';
import { Warranty } from 'lib/types/models';
import { useState } from 'react';
import {
  getSGRWarrantyRequestMissingApprovals,
  updateSGRWarrantyRequestComitee,
  approveSGRWarrantyRequest,
  rejectSGRWarrantyRequest,
  deleteSGRWarrantyRequestApproval,
} from 'lib/api/sgr';
import { CommitteeService } from 'pages/Finance/Components/Financing/common/Comittee/types';

// Create a committee service for SGR warranties
const createSGRWarrantyCommitteeService = (
  warranty: Warranty,
  refreshWarranty?: () => void,
): CommitteeService => ({
  requestId: warranty.id,
  canEdit: warranty.status === 'IN_REVIEW',
  isRejected: warranty.status === 'REJECTED',
  getMissingApprovals: async (dispatch, requestId) => {
    const response = await getSGRWarrantyRequestMissingApprovals(
      dispatch,
      requestId,
    );
    return response;
  },
  approve: async (dispatch, requestId, data) => {
    await approveSGRWarrantyRequest(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  reject: async (dispatch, requestId, data) => {
    await rejectSGRWarrantyRequest(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  updateCommittee: async (dispatch, requestId, data) => {
    await updateSGRWarrantyRequestComitee(dispatch, requestId, data);
    if (refreshWarranty) refreshWarranty();
  },
  deleteApproval: async (dispatch, requestId, approvalId) => {
    await deleteSGRWarrantyRequestApproval(dispatch, requestId, approvalId);
    if (refreshWarranty) refreshWarranty();
  },
});

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: Warranty;
  refreshWarranty?: () => void;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  refreshWarranty,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  if (!warranty) return null;

  const committeeService = createSGRWarrantyCommitteeService(
    warranty,
    refreshWarranty,
  );

  const tabs = [
    {
      label: 'Resumen',
      component: isEditing ? (
        <EditableSummary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ) : (
        <Summary warranty={warranty} setIsEditing={setIsEditing} />
      ),
    },
    {
      label: 'Aprobación',
      component: <Comitee committeeService={committeeService} />,
    },
    {
      label: 'Riesgo',
      component: (
        <RiskReviews item={warranty} requestTypes={['sgr-warranty-request']} />
      ),
    },
    {
      label: 'Documentos',
      component: <File warranty={warranty} />,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={isEditing ? 'lg' : 'md'}
      fullWidth
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        <Close />
      </IconButton>
      <div style={{ padding: '0 20px' }}>
        <EnhancedTab title={`Garantía #${warranty.id}`} tabs={tabs} />
      </div>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
